<template>
  <div id="resource-occupation-dashboard" class="resource-occupation-dashboard">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-2 header-block">
      <div class="d-flex align-items-center">
        <h2 class="mr-2 mb-0 page-title">Ocupação de Recursos</h2>
        <div class="dashboard-tabs ml-2">
          <span class="tab-item active"></span>
        </div>
      </div>
      <b-button class="btn-filters" variant="outline-primary" size="sm" @click="filtersOpen = true">
        <feather-icon icon="FilterIcon" class="mr-50" />
        Filtros
      </b-button>
    </div>

    <div v-if="loading" class="text-center p-5">
      <b-spinner variant="primary" label="Loading..."></b-spinner>
    </div>

    <template v-else>
      <!-- Top Metrics -->
      <b-row class="match-height mb-2">
        <b-col v-if="dashboardData.delayForecast" md="3">
          <delay-forecast-card :data="dashboardData.delayForecast" />
        </b-col>
        <b-col :md="dashboardData.delayForecast ? 5 : 6">
          <operational-status-card :data="dashboardData.operationalStatus" />
        </b-col>
        <b-col :md="dashboardData.delayForecast ? 4 : 6">
          <resource-occupation-card :data="dashboardData.resourceOccupation" />
        </b-col>
      </b-row>

      <!-- Resource List -->
      <resource-list :centers="dashboardData.centers" />
    </template>

    <b-sidebar right shadow bg-variant="white" width="320px" v-model="filtersOpen" backdrop>
      <template #header>
        <div class="d-flex justify-content-between align-items-center w-100 px-1 py-1">
          <span class="font-weight-bold">Filtros</span>
          <span style="cursor: pointer" @click="filtersOpen = false">
            <feather-icon icon="XIcon" />
          </span>
        </div>
      </template>

      <div class="filters-card">
        <div class="filter">
          <label class="filter-label">Filtro Rápido:</label>
          <div class="quick-filter-buttons">
            <button v-for="days in quickFilterDays" :key="days"
              :class="['quick-filter-btn', { active: filterDays === days }]" @click="setQuickFilter(days)">
              {{ days }} dias
            </button>
          </div>
        </div>
        <div class="filter">
          <label class="filter-label">De:</label>
          <input type="date" v-model="startDate" class="date-input-sidebar" @change="onManualDateChange" />
        </div>
        <div class="filter">
          <label class="filter-label">Até:</label>
          <input type="date" v-model="endDate" class="date-input-sidebar" @change="onManualDateChange" />
        </div>
        <div class="filter" v-for="f in visibleFilters" :key="f.key">
          <label class="filter-label">{{ f.label }}</label>

          <b-form-select v-model="f.value" :options="f.options" size="sm" />
        </div>
      </div>

      <template #footer>
        <div class="d-flex justify-content-end px-1 pb-1">
          <b-button size="sm" class="mr-50" variant="outline-secondary" @click="clearFilters">
            Limpar
          </b-button>
          <b-button size="sm" variant="primary" @click="applyFilters">
            Aplicar
          </b-button>
        </div>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import { BRow, BCol, BButton, BSpinner, BSidebar, BFormSelect } from 'bootstrap-vue'
import DelayForecastCard from './components/DelayForecastCard.vue'
import OperationalStatusCard from './components/OperationalStatusCard.vue'
import ResourceOccupationCard from './components/ResourceOccupationCard.vue'
import ResourceList from './components/ResourceList.vue'
import moment from 'moment'
import { useFilters } from "@/views/hooks/useFilters";
import { loadResources } from '@/views/utils/getResources';

export default {
  components: {
    BRow,
    BCol,
    BButton,
    BSidebar,
    BFormSelect,
    BSpinner,
    DelayForecastCard,
    OperationalStatusCard,
    ResourceOccupationCard,
    ResourceList
  },
  data() {
    const filtersHelper = useFilters();

    return {
      loading: true,
      currentSite: this.$cookies.get("userInfo")?.currentSite,
      filtersOpen: false,
      quickFilterDays: [7, 45, 60],
      filterDays: 7,
      startDate: null,
      endDate: null,
      filtersHelper,
      filters: filtersHelper.createFilters(),
      dashboardData: {
        delayForecast: null,
        operationalStatus: {
          totalOrders: 0,
          distribution: []
        },
        resourceOccupation: [],
        centers: []
      },
      areaMap: {},
      resourceMap: {}
    }
  },
  mounted() {
    this.loadWorkcenters();
    this.loadResources();
    this.fetchData()
  },
  computed: {
    visibleFilters() {
      return this.filters.filter(f => !['year', 'month', 'day'].includes(f.key));
    },
  },
  methods: {
    setQuickFilter(days) {
      this.filterDays = days
      const today = moment().format('YYYY-MM-DD')
      const start = moment().subtract(days, 'days').format('YYYY-MM-DD')
      this.startDate = start
      this.endDate = today
    },
    onManualDateChange() {
      this.filterDays = null
    },
    clearFilters() {
      this.filterDays = null
      this.startDate = null
      this.endDate = null
    },
    applyFilters() {
      this.filtersOpen = false
      this.fetchData()
    },
    async fetchData() {
      if (!this.currentSite) {
        console.error("No site selected")
        this.loading = false
        return
      }

      this.loading = true
      try {
        const prefix = this.$router.currentRoute.params.prefix

        const selectedResource = this.filtersHelper.getFilterValue(this.filters, 'resource');
        const selectedArea = this.filtersHelper.getFilterValue(this.filters, 'area');
        console.log(selectedResource, selectedArea);
        const resourceId = selectedResource ? this.resourceMap[selectedResource] : null;
        const areaId = selectedArea ? this.areaMap[selectedArea] : null;
        console.log(resourceId, areaId);
        // Parallel API calls
        const [opsSummary] = await Promise.all([
          this.$http2.get(`/api/dashboards/operator-summary?site_id=${this.currentSite}${this.startDate ? `&start_date=${this.startDate}` : ''}${this.endDate ? `&end_date=${this.endDate}` : ''}${this.filterDays ? `&filter_days=${this.filterDays}` : ''}${resourceId ? `&resource_id=${resourceId}` : ''}${areaId ? `&area_id=${areaId}` : ''}`)
        ])

        this.dashboardData.delayForecast = opsSummary.data.data.delayForecast
        this.dashboardData.operationalStatus = opsSummary.data.data.operationalStatus
        this.dashboardData.resourceOccupation = opsSummary.data.data.resourceOccupation
        this.dashboardData.centers = opsSummary.data.data.centers
      } catch (error) {
        console.error("Error fetching dashboard data:", error)
        // Keep initial state on error or show toast
      } finally {
        this.loading = false
      }
    },
    async loadWorkcenters() {
      const siteId = this.filtersHelper.getCurrentSiteId(this.$cookies);

      if (!siteId) {
        console.warn("Nenhum site selecionado para carregar dados de OEE.");
        return;
      }

      const workcenters = await this.$http2.get("/api/myconfig/workcenters", {
        params: {
          SiteId: siteId
        }
      });

      const areaNames = workcenters?.data?.data?.map(item => item?.workcenterName) || [];
      this.filtersHelper.updateFilterOptions(this.filters, 'area', areaNames, true);

      this.areaMap = Object.fromEntries(workcenters?.data?.data?.map(item => [item.workcenterName, item.workcenterId]) || []);
    },
    async loadResources() {
      const siteId = this.filtersHelper.getCurrentSiteId(this.$cookies);

      if (!siteId) {
        console.warn("Nenhum site selecionado para carregar dados de OEE.");
        return;
      }

      const resources = await loadResources(siteId, this.$http2);
      const resourceNames = resources?.map(item => item?.name) || [];

      this.filtersHelper.updateFilterOptions(this.filters, 'resource', resourceNames, true);

      this.resourceMap = Object.fromEntries(resources?.map(item => [item.name, item.id]) || []);
    },
  }
}
</script>

<style lang="scss" scoped>
#resource-occupation-dashboard {
  .header-block {
    padding: 10px 14px;
  }

  .page-title {
    font-size: 18px;
    font-weight: 700;
    color: #4c4541;
  }

  .filters-card {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 12px;

    .filter+.filter {
      margin-top: 8px;
    }

    .filter-label {
      display: block;
      font-weight: 600;
      font-size: 12px;
      margin-bottom: 6px;
    }

    .date-input-sidebar {
      width: 100%;
      padding: 6px 8px;
      border: 1px solid #d8d6de;
      border-radius: 4px;
      font-size: 14px;
      color: #6c757d;
      background-color: #fff;
      cursor: pointer;
      transition: border-color 0.2s;

      &:hover {
        border-color: #9f5714;
      }

      &:focus {
        outline: none;
        border-color: #9f5714;
        box-shadow: 0 0 0 2px rgba(243, 129, 26, 0.1);
      }
    }

    .quick-filter-buttons {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 6px;
    }

    .quick-filter-btn {
      padding: 6px 10px;
      border: 1px solid #d8d6de;
      border-radius: 4px;
      background-color: #fff;
      color: #6c757d;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        border-color: #9f5714;
        color: #9f5714;
      }

      &.active {
        background-color: #9f5714;
        border-color: #9f5714;
        color: #fff;
      }
    }
  }

  .btn-filters {
    border-color: #9f5714 !important;
    color: #9f5714 !important;

    &:hover {
      background-color: #9f5714 !important;
      color: #fff !important;
    }
  }

  .dashboard-tabs {
    font-size: 1.1rem;

    .tab-item {
      cursor: pointer;
      padding-bottom: 2px;

      &.active {
        color: #ff9f43;
        border-bottom: 2px solid #ff9f43;
        font-weight: bold;
      }
    }
  }

  ::v-deep .b-sidebar-footer {
    .btn-primary {
      background-color: #9f5714 !important;
      border-color: #9f5714 !important;

      &:hover {
        background-color: #7d4510 !important;
        border-color: #7d4510 !important;
      }
    }
  }
}
</style>
